<template>
    <div>
        <EmailsFieldAddDialog
            v-if="group.isShowAddVisible"
            :emails="emailIntegrations"
            :selected-email-integration-ids="selectedEmailIntegrationIds"
            :company-id="accountId"
            @save="saveEmails"
            @close="group.isShowAddVisible = false"
            @add-new-email="addNewEmail"
        />
        <EmailsFieldEditDialog
            v-if="editEmailSettingIndex !== null"
            :email="selectedEmailSetting"
            :email-integration="selectedEmailSettingIntegration"
            @save="saveEmail"
            @close="editEmailSettingIndex = null"
            @delete="removeSelectedEmailSetting(editEmailSettingIndex)"
        />
        <data-table
            :headers="EmailIntegrationModule.getEmailIntegrationTableHeaders()"
            :items="items"
            row-clickable
            @click:row-index="editContact"
        >
            <template v-slot:item.email="{ item }">
                <v-chip
                    pill
                    color="g2"
                    class="px-4"
                    :class="item.is_default ? 'pr-3' : ''"
                >
                    {{ item.email }}
                    <v-avatar
                        v-if="item.is_default"
                        right
                    >
                        <v-btn
                            icon
                            right
                            small
                            color="secondary"
                        >
                            <v-icon size="18">star</v-icon>
                        </v-btn>
                    </v-avatar>
                </v-chip>
            </template>
            <template v-slot:item.visibility="{ item }">
                {{ $t('Client members') }}
            </template>
        </data-table>
    </div>
</template>

<script>
import {computed, onMounted, ref} from "@vue/composition-api";
import EmailsFieldAddDialog from "@/components/CustomField/EmailsField/EmailsFieldAddDialog";
import ApiEmailAutomationEmail from "@/api/email_automation/email_integration";
import CustomFormHeader from "@/components/CustomForm/CustomFormHeader";
import EmailsFieldEditDialog from "@/components/CustomField/EmailsField/EmailsFieldEditDialog";
import EmailIntegrationModule from "@/modules/emailIntegration/emailIntegration"

export default {
    name: "EmailsField",
    components: {
        EmailsFieldAddDialog,
        CustomFormHeader,
        EmailsFieldEditDialog
    },
    props: {
        accountId: {
            type: Number,
            default: 0
        },
        field: {
            type: Object,
            required: true
        },
        value: {
            type: Array,
            default: null
        },
        errorMessages: {
            type: String,
            default: null
        },
        inputCssClass: {
            type: String,
            default: ""
        },
        groupName: {
            type: String,
            required: true
        },
        group: {
            type: Object,
            required: true
        }
    },
    setup(props, ctx) {
        const emailIntegrations = ref([]);

        const editEmailSettingIndex = ref(null);

        const selectedEmailIntegrationIds = computed(() => props.value ? props.value.map(x => x.email_integration_id) : [])
        const selectedEmailSetting = computed(() => editEmailSettingIndex.value !== null ? props.value[editEmailSettingIndex.value] : null)
        const selectedEmailSettingIntegration = computed(() => selectedEmailSetting.value ? emailIntegrations.value.find(x => x.id === selectedEmailSetting.value.email_integration_id) : null)

        const items = computed(() => {
            if (!props.value) {
                return []
            }

            let ret = []

            for (const emailSetting of props.value) {
                const emailIntegration = emailIntegrations.value.find(x => x.id === emailSetting.email_integration_id)
                if (!emailIntegration) {
                    continue
                }

                ret.push({
                    email: emailIntegration.email,
                    sender_name: emailSetting.sender_name ? emailSetting.sender_name : emailIntegration.sender_name,
                    visibility: 'client',
                    reply_to_email: emailSetting.reply_to_email ? emailSetting.reply_to_email : emailIntegration.reply_to_email,
                    is_default: emailSetting.is_default
                })
            }

            return ret
        })

        const editContact = index => {
            editEmailSettingIndex.value = index
        };

        onMounted(async () => {
            emailIntegrations.value = (
                await ApiEmailAutomationEmail.getEmailIntegrations(props.accountId)
            ).email_automation_emails.filter(x => x.visibility === 'client')
        })

        const saveEmails = (data) => {
            const selectedEmailIntegrationIds = data.emailIntegrationIds
            const clientDefaultAddressEmailIntegrationId = data.clientDefaultAddressEmailIntegrationId

            const mappedClientEmails = []

            for (const selectedEmailIntegrationId of selectedEmailIntegrationIds) {
                const existingValue = props.value ? props.value.find(x => x.email_integration_id === selectedEmailIntegrationId) : null

                const isDefault = selectedEmailIntegrationId === clientDefaultAddressEmailIntegrationId ? 1 : 0

                if (existingValue) {
                    if (clientDefaultAddressEmailIntegrationId) {
                        existingValue.is_default = isDefault
                    }
                    mappedClientEmails.push(existingValue)
                } else {
                    mappedClientEmails.push({
                        email_integration_id: selectedEmailIntegrationId,
                        id: null,
                        is_default: isDefault,
                        reply_to_email: null,
                        sender_name: null
                    })
                }
            }

            ctx.emit("change", mappedClientEmails);

            props.group.isShowAddVisible = false
        }

        const saveEmail = (data) => {
            let valueCopy = props.value.slice()

            if (data.is_default) {
                valueCopy = valueCopy.map(x => {
                    x.is_default = false

                    return x
                })
            }

            valueCopy.splice(editEmailSettingIndex.value, 1, data)

            ctx.emit('change', valueCopy)
            editEmailSettingIndex.value = null
        }

        const addNewEmail = (email) => {
            let index = emailIntegrations.value.findIndex(x => x.id === email.id)
            if (index >= 0) {
                let newEmailIntegrations = emailIntegrations.value.slice()
                newEmailIntegrations[index] = email
                emailIntegrations.value = newEmailIntegrations
            } else {
                emailIntegrations.value.unshift(email)
            }
        }

        const removeSelectedEmailSetting = (index) => {
            let updatedEmailSettings = props.value.slice();
            updatedEmailSettings.splice(index, 1);
            ctx.emit('change', updatedEmailSettings)

            editEmailSettingIndex.value = null;
        }

        return {
            EmailIntegrationModule,
            editContact,
            emailIntegrations,
            saveEmails,
            items,
            selectedEmailIntegrationIds,
            editEmailSettingIndex,
            selectedEmailSetting,
            selectedEmailSettingIntegration,
            saveEmail,
            addNewEmail,
            removeSelectedEmailSetting
        }
    }
};
</script>